import { useEffect, useMemo, useRef, useState } from 'react';
import ExpandIcon from '../../assets/exportableIcons/expand';
import CardContainer from '../../component/cardContainer/cardContainer';
import IconsDock from '../../component/iconsDock/iconsDock';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import styles from './dashboard.module.css';
import CollapseIcon from '../../assets/exportableIcons/collapse';
import { Trans } from 'react-i18next';
import {
  DashboardViews,
  GroupedPlayersData,
  PlayerWithRisk,
} from './dashboard.type';
import { usePlayerSummary } from '../../hooks/usePlayerSummary';
import { PlayerPageLimit } from '../../utils/helper.type';
import { HarmObserved, PlayerSummary, Sweep } from '@inplay/models';
import { PlayerRisk } from '../../component/playerIcon/playerIcon.types';
import ModalComponent from '../../component/modal/modal';
import LiveIcon from '../../assets/exportableIcons/liveIcon';
import HistoryIcon from '../../assets/exportableIcons/historyIcon';
import LegendsIcon from '../../assets/exportableIcons/legendsIcon';
import SettingsIcon from '../../assets/exportableIcons/settingsIcon';
import { IconOptions } from '../../component/iconsDock/iconsDock.types';
import {
  IconTooltip,
  RenderHourlySweepContent,
  RenderLegendContent,
  RenderSummaryContent,
} from './dashboardHelper';
import useResizeObserver from '@react-hook/resize-observer';
import { useOrganizationContext } from '../../service/context/organizationContext';
import SearchIcon from '../../assets/exportableIcons/searchIcon';
import FilterIcon from '../../assets/exportableIcons/filterIcon';
import { useSweepsSummary } from '../../hooks/useSweepData';
import { getHourRange } from '../../utils/helper';
import RadialLayout from './radialLayout/radialLayout';
import RadialIcon from '../../assets/exportableIcons/radialIcon';
import ListIcon from '../../assets/exportableIcons/listIcon';
import ListLayout from './listLayout/listLayout';
import FullscreenModal from '../../component/fullscreenModal/fullscreenModal';
import DropdownMenu from '../../component/dropDownMenu/dropDownMenu';
import PlayerDetails from '../../component/playerDetails/playerDetails';
import KebabIcon from '../../assets/kebab.png';
import { isUndefined } from 'lodash';

const Dashboard = () => {
  const { isTablet } = useResponsiveLayout();
  const [expand, setExpand] = useState<boolean>(false);
  const [view, setView] = useState<DashboardViews>(DashboardViews.RADIAL);
  const { selectedVenueBranch, loading } = useOrganizationContext();
  const selectedBranchIDs: string[] = useMemo(() => {
    return selectedVenueBranch ? [selectedVenueBranch._id] : [];
  }, [selectedVenueBranch]);
  const { allPlayers, playerLoading, decodedLoginData } = usePlayerSummary(
    selectedBranchIDs,
    undefined,
    undefined,
    undefined,
    PlayerPageLimit.LIMIT,
    true,
  );
  const [playersData, setPlayersData] = useState<GroupedPlayersData>({
    low: [],
    intervention_offered: [],
    intervention_required: [],
    excluded: [],
  });
  const [showPlayerDetails, setShowPlayerDetails] = useState<
    PlayerWithRisk | undefined
  >(undefined);
  const [showActionMenu, setShowActionMenu] = useState<boolean>(false);

  const [showLegend, setShowLegend] = useState<boolean>(false);
  const viewContainerRef = useRef<HTMLDivElement>(null);
  const [viewContainerDimensions, setViewContainerDimensions] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });
  const realtimeSweepEventsEnabled: boolean = false;
  const { fetchSweepSummary } = useSweepsSummary(
    {
      startTime: getHourRange().hourStartTime,
      endTime: getHourRange().hourEndTime,
      venueBranchIds: selectedBranchIDs,
    },
    realtimeSweepEventsEnabled,
  );
  const [sweepsSummary, setSweepsSummary] = useState<Sweep[]>([]);
  const [isLargeVenueBranch, setIsLargeVenueBranch] = useState<boolean>(false);
  const [isVenueBranchOvercrowded, setIsVenueBranchOvercrowded] =
    useState<boolean>(false);

  const refreshSweepData = (): void => {
    if (!selectedBranchIDs.length) return;
    const sweepDataFetch = async (): Promise<void> => {
      const data: Sweep[] =
        (await fetchSweepSummary({
          startTime: getHourRange().hourStartTime,
          endTime: getHourRange().hourEndTime,
          venueBranchIds: selectedBranchIDs,
        })) ?? [];
      setSweepsSummary(data);
    };
    sweepDataFetch();
  };

  useEffect(() => {
    refreshSweepData();
  }, [selectedBranchIDs]);

  useEffect(() => {
    if (!isUndefined(selectedVenueBranch)) {
      const isLargeBranch: boolean =
        selectedVenueBranch &&
        selectedVenueBranch?.numberOfGamblingMachines === '18+';
      console.log(
        `[Dashboard](useEffect) VenueBranch is not undefined. Setting the isLargeVenueBranch state to ${isLargeBranch}`,
      );
      setIsLargeVenueBranch(isLargeBranch);
      console.log(
        `[Dashboard](useEffect) VenueBranch is not undefined. Setting the isVenueBranchOvercrowded state to ${
          !isLargeBranch && allPlayers.length > 18
        }`,
      );
      !isLargeBranch && allPlayers.length > 18
        ? setIsVenueBranchOvercrowded(true)
        : setIsVenueBranchOvercrowded(false);
    }
  }, [selectedVenueBranch]);

  useEffect(() => {
    if (isLargeVenueBranch || isVenueBranchOvercrowded) {
      setView(DashboardViews.LIST);
    }
  }, [isLargeVenueBranch, isVenueBranchOvercrowded]);

  useResizeObserver(viewContainerRef, (entry) => {
    setViewContainerDimensions({
      width: entry.contentRect.width,
      height: entry.contentRect.height,
    });
  });

  useEffect(() => {
    const initialPlayersData: GroupedPlayersData = {
      low: [],
      intervention_offered: [],
      intervention_required: [],
      excluded: [],
    };
    if (!playerLoading && allPlayers) {
      allPlayers.forEach((player: PlayerSummary) => {
        const tempPlayer: PlayerWithRisk = { ...player, risk: PlayerRisk.LOW };
        if (player.harmObserved === HarmObserved.YES) {
          tempPlayer.risk = PlayerRisk.INTERVENTION_REQUIRED;
          initialPlayersData.intervention_required.push(tempPlayer);
        } else {
          initialPlayersData.low.push(tempPlayer);
        }
      });
    }
    setPlayersData(initialPlayersData);
  }, [allPlayers, playerLoading, decodedLoginData]);

  const icons: IconOptions = {
    dock1: [
      {
        icon: <LiveIcon active />,
        onClick: () => {}, // TODO: Add live functionality
        label: 'iconLabels.currentPlayers',
      },
      {
        icon: <HistoryIcon disable />,
        onClick: () => {}, // TODO: Add history functionality
        label: 'iconLabels.playersHistory',
        disabled: true,
      },
    ],
    dock2: [
      {
        icon: <SearchIcon disable />,
        onClick: () => {}, // TODO: Add search functionality
        label: 'iconLabels.search',
        disabled: true,
      },
      {
        icon: <FilterIcon disable />,
        onClick: () => {}, // TODO: Add filter functionality
        label: 'iconLabels.filter',
        disabled: true,
      },
      {
        icon: <LegendsIcon active={showLegend} />,
        onClick: () => {
          setShowLegend((prev) => !prev);
        },
        label: 'iconLabels.legend',
      },
      {
        icon: <SettingsIcon />,
        onClick: () => {}, // TODO: Add settings functionality
        label: 'iconLabels.settings',
        hide: true,
      },
    ],
  };

  return (
    <main className={`${styles.dashboard} ${isTablet && styles.tablet}`}>
      <section className={styles.mainContainer}>
        <CardContainer
          className={`${styles.mainCard} ${expand && styles.expand} `}
          background="bg-DASHBOARD_THEME_COLOR"
        >
          <div className={styles.header}>
            <div className={styles.title}>
              <Trans i18nKey={'dashboardNew.title'} />
            </div>
            <button
              className={styles.expandBtn}
              onClick={() => setExpand((prev) => !prev)}
            >
              <IconTooltip
                text={expand ? 'iconLabels.collapse' : 'iconLabels.expand'}
                className={styles.tooltipLeft}
              />
              {expand ? <CollapseIcon /> : <ExpandIcon />}
            </button>
          </div>
          <div className={styles.viewContainer} ref={viewContainerRef}>
            {showPlayerDetails && (
              <FullscreenModal
                show={!!showPlayerDetails}
                onBack={() => setShowPlayerDetails(undefined)}
                title="playerDetails.details"
                headerRight={
                  <div className={styles.headerRightContent}>
                    <span>
                      Player 1/
                      {playersData.excluded.length +
                        playersData.intervention_offered.length +
                        playersData.intervention_required.length +
                        playersData.low.length}
                    </span>
                    <div className="flex ml-2 min-h-[24px]">
                      <img
                        src={KebabIcon}
                        alt="Kebab icon"
                        className="w-5 h-5 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowActionMenu(true);
                        }}
                      />
                    </div>

                    {showActionMenu && (
                      <div className="relative top-full right-full z-50">
                        <DropdownMenu
                          className="whitespace-nowrap"
                          options={[]}
                          onSelect={() => {}}
                          onClose={() => setShowActionMenu(false)}
                        />
                      </div>
                    )}
                  </div>
                }
              >
                <PlayerDetails player={showPlayerDetails} />
              </FullscreenModal>
            )}
            {loading || playerLoading ? (
              <img src="./loader.gif" alt="" />
            ) : view === DashboardViews.RADIAL ? (
              <RadialLayout
                playersData={playersData}
                playerLoading={playerLoading}
                dimensions={viewContainerDimensions}
                onPlayerClick={(player: PlayerWithRisk) => {
                  setShowPlayerDetails(player);
                }}
              />
            ) : (
              <ListLayout
                playersData={playersData}
                onPlayerClick={(player: PlayerWithRisk) => {
                  setShowPlayerDetails(player);
                }}
              />
            )}
          </div>
          <div className={styles.footer}>
            <div className={styles.btnContainer}>
              <button
                className={styles.radial}
                onClick={() => {
                  if (view === DashboardViews.LIST) {
                    setView(DashboardViews.RADIAL);
                  }
                }}
                disabled={isLargeVenueBranch || isVenueBranchOvercrowded}
              >
                <IconTooltip
                  text="iconLabels.radialLayout"
                  className={styles.tooltipLeft}
                />
                <RadialIcon
                  active={view === DashboardViews.RADIAL}
                  disable={isLargeVenueBranch || isVenueBranchOvercrowded}
                />
              </button>
              <button
                className={styles.list}
                onClick={() => {
                  if (view === DashboardViews.RADIAL) {
                    setView(DashboardViews.LIST);
                  }
                }}
              >
                <IconTooltip
                  text="iconLabels.listLayout"
                  className={styles.tooltipTop}
                />
                <ListIcon active={view === DashboardViews.LIST} />
              </button>
            </div>
          </div>
          <IconsDock icons={icons} />
        </CardContainer>
      </section>
      <section className={`${styles.statsContainer}`}>
        <CardContainer shadow className={styles.statCard}>
          <div className={styles.title}>
            <Trans i18nKey={'dashboardNew.summary'} />
          </div>
          {RenderSummaryContent(playersData)}
        </CardContainer>
        <CardContainer shadow className={styles.statCard}>
          <div className={styles.title}>
            <Trans i18nKey={'dashboardNew.hourly'} />
          </div>
          {RenderHourlySweepContent(sweepsSummary)}
        </CardContainer>
      </section>
      <ModalComponent
        dialogClassName={'top-[0px] right-[0px] h-full w-[500px]'}
        footerHidden={true}
        isShowClose={true}
        open={showLegend}
        onClose={() => {
          setShowLegend(false);
        }}
        onToggle={() => {
          setShowLegend(false);
        }}
        title={<Trans i18nKey={'dashboardNew.legend'} />}
      >
        {RenderLegendContent()}
      </ModalComponent>
    </main>
  );
};

export default Dashboard;
